import React, { useState, useEffect, useCallback, useTransition, useMemo } from "react";
import { FaArrowLeft, FaSearch } from "react-icons/fa";
import { getMenuByRestaurant } from '../../../api/menuApi';
import { useSignalREvent } from '../../../hooks/useSignalREvent';
import { CartItem } from "./CartPage";
import { CustomerDetails } from "../types";

// Custom SVG icons for Veg and Non-Veg
const VegIcon = (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-label="Vegetarian">
    <rect x="3" y="3" width="18" height="18" rx="2" fill="none" stroke="#22c55e" strokeWidth="2" />
    <circle cx="12" cy="12" r="5" fill="#22c55e" />
  </svg>
);

const NonVegIcon = (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-label="Non-Vegetarian">
    <rect x="3" y="3" width="18" height="18" rx="2" fill="none" stroke="#8b5c2c" strokeWidth="2" />
    <path d="M12 7L17 17H7L12 7Z" fill="#8b5c2c" />
  </svg>
);

// Define the static subcategory options for filtering, with labels and emojis or icons
const SUBCATEGORY_OPTIONS = [
  { type: 'all', label: 'All', icon: '🍽️' },
  { type: 'veg', label: 'Veg', icon: VegIcon },
  { type: 'nonveg', label: 'Non-Veg', icon: NonVegIcon },
  { type: 'vegan', label: 'Vegan', icon: '🌱' },
  { type: 'jain', label: 'Jain', icon: '🙏' },
];

// Updated MenuItem interface to match CustomerMenu
interface MenuItem {
  id: string;
  restaurantGuid: string;
  name: string;
  cuisineType?: string[] | null;
  shortDescription?: string | null;
  description: string | null;
  subcategory?: string | null;
  category: string;
  dishTags?: string[] | null;
  spiceLevel?: number | null;
  price: number;
  discountPrice?: number | null;
  taxesApplicable: boolean;
  servingSize?: string | null;
  availableDays?: string[] | null;
  availableFrom?: string | null;
  availableTo?: string | null;
  keyIngredients?: string[] | null;
  allergens?: string[] | null;
  imageUrl?: string | null;
  itemImage?: string | null;
  isAvailable: boolean;
  isBestseller?: boolean | null;
  estimatedTimeInMin?: number | null;
}

type Props = {
  customerDetails?: CustomerDetails;
  tableId: string;
  restaurantGuid: string;
  cartItems: CartItem[];
  onBack: () => void;
  onAddToCart: (item: MenuItem, quantity: number) => void;
  onUpdateCartQuantity: (itemId: string, quantity: number) => void;
};

const NewOrderPage: React.FC<Props> = ({
  customerDetails = {},
  tableId,
  restaurantGuid,
  cartItems,
  onBack,
  onAddToCart,
  onUpdateCartQuantity
}) => {
  const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('All');
  const [selectedSubcategory, setSelectedSubcategory] = useState<string>('All');
  const [searchTerm, setSearchTerm] = useState("");
  // eslint-disable-next-line
  const [isPending, startTransition] = useTransition();

  const categories = useMemo(() => {
    const unique = new Set(menuItems.map(m => m.category || 'Uncategorized'));
    return ['All', ...Array.from(unique)];
  }, [menuItems]);

  const fetchMenu = useCallback(async (isInitial = false) => {
    if (!restaurantGuid) return setError('Restaurant not found.');

    try {
      if (isInitial) setLoading(true);
      const menuData = await getMenuByRestaurant(restaurantGuid);
      if (!Array.isArray(menuData)) throw new Error('Invalid menu data');

      const existing = JSON.stringify(menuItems);
      const incoming = JSON.stringify(menuData);
      if (existing !== incoming) startTransition(() => setMenuItems(menuData));
      localStorage.setItem('menu', JSON.stringify(menuData));
    } catch (err) {
      console.error(err);
      setError('Failed to load menu');
    } finally {
      if (isInitial) setLoading(false);
    }
    // eslint-disable-next-line
  }, [restaurantGuid]);

  useEffect(() => {
    const cachedMenu = localStorage.getItem('menu');
    if (cachedMenu) {
      try {
        const parsed = JSON.parse(cachedMenu);
        if (Array.isArray(parsed)) setMenuItems(parsed);
      } catch { console.warn('Invalid cached menu'); }
    }
    fetchMenu(true);
  }, [fetchMenu]);

  useSignalREvent('CustomerMenuUpdate', 'CustomerMenu', () => fetchMenu(false));

  const filteredItems = useMemo(() => {
    return menuItems.filter(item => {
      const matchesCategory = selectedCategory === 'All' || item.category === selectedCategory;
      const matchesSubcategory = selectedSubcategory === 'All' || item.subcategory?.toLowerCase() === selectedSubcategory.toLowerCase();
      const matchesSearch = item.name.toLowerCase().includes(searchTerm.toLowerCase())
        || item.description?.toLowerCase().includes(searchTerm.toLowerCase())
        || item.shortDescription?.toLowerCase().includes(searchTerm.toLowerCase());
      return matchesCategory && matchesSubcategory && matchesSearch && item.isAvailable;
    });
  }, [menuItems, selectedCategory, selectedSubcategory, searchTerm]);

  const handleAddToCart = (item: MenuItem) => onAddToCart(item, 1);
  const handleIncrementQuantity = (itemId: string) => {
    const cartItem = cartItems.find(i => i.id === itemId);
    if (cartItem) onUpdateCartQuantity(itemId, cartItem.quantity + 1);
  };
  const handleDecrementQuantity = (itemId: string) => {
    const cartItem = cartItems.find(i => i.id === itemId);
    if (!cartItem) return;
    if (cartItem.quantity === 1) onUpdateCartQuantity(itemId, 0);
    else onUpdateCartQuantity(itemId, cartItem.quantity - 1);
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100 dark:bg-gray-900">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
        <p className="text-gray-600 dark:text-gray-400">Loading menu...</p>
      </div>
    </div>
  );

  if (error) return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100 dark:bg-gray-900">
      <div className="text-center">
        <p className="text-red-500 mb-4">{error}</p>
        <button
          onClick={() => fetchMenu(true)}
          className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition"
        >
          Retry
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-gray-900">
      {/* Header */}
      <header className="sticky top-0 z-20 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 transition-colors duration-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center gap-3 sm:gap-4">
          <button
            onClick={onBack}
            className="p-2 rounded-lg text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700 transition"
          >
            <FaArrowLeft size={16} />
          </button>
          <div>
            <h1 className="text-lg sm:text-xl md:text-2xl font-extrabold bg-clip-text text-transparent bg-gradient-to-r from-orange-500 to-amber-600 dark:from-orange-400 dark:to-amber-500">
              More Orders
            </h1>
            <p className="text-xs sm:text-sm mt-1 text-gray-600 dark:text-gray-400">
              Table {tableId} • {customerDetails.customerName || 'Guest'}
            </p>
          </div>
        </div>
      </header>

      {/* Search & Filters */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-3 sm:p-4 mb-4">
          {/* Search */}
          <div className="relative mb-3">
            <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
            <input
              type="text"
              placeholder="Search menu items..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600
                         bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400
                         focus:outline-none focus:ring-2 focus:ring-orange-500 transition"
            />
          </div>

          {/* Category Filters */}
          {categories.length > 1 && (
            <div className="flex flex-wrap justify-center gap-2 mb-3">
              {categories.map(category => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-4 py-1 rounded-full text-sm border transition
                    ${selectedCategory === category
                      ? 'bg-orange-500 text-white'
                      : 'bg-white border-orange-300 text-orange-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300'
                    }`}
                >
                  {category}
                </button>
              ))}
            </div>
          )}

          {/* Subcategory Filters */}
          <div className="flex flex-wrap justify-center gap-2">
            {SUBCATEGORY_OPTIONS.map(option => (
              <button
                key={option.type}
                onClick={() => setSelectedSubcategory(option.label)}
                className={`flex items-center gap-1 px-3 py-1 rounded-full border text-sm transition
                  ${selectedSubcategory.toLowerCase() === option.label.toLowerCase()
                    ? 'bg-orange-500 text-white shadow-md'
                    : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
              >
                {option.icon} {option.label}
              </button>
            ))}
          </div>
        </div>

        {/* Menu Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filteredItems.map(item => {
            const isDiscounted = item.discountPrice && item.discountPrice < item.price;
            const discountPercentage = isDiscounted
              ? Math.round(((item.price - item.discountPrice!) / item.price) * 100)
              : 0;

            return (
              <div key={item.id} className={`bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 flex flex-col justify-between
                ${!item.isAvailable ? 'opacity-50 pointer-events-none' : ''}`}>

                {/* Item Info */}
                <div className="flex flex-col h-full">
                  <h3 className="text-sm sm:text-base font-semibold text-gray-900 dark:text-white mb-1 truncate">{item.name}</h3>
                  <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mb-2 truncate">
                    {item.shortDescription || item.description || 'NA'}
                  </p>
                  <div className="flex items-center gap-2 mb-3 flex-wrap">
                    <span className="text-sm sm:text-base font-bold text-orange-600 dark:text-orange-400">
                      ₹{item.discountPrice?.toFixed(2) || item.price.toFixed(2)}
                    </span>
                    {isDiscounted && (
                      <>
                        <span className="text-xs line-through text-gray-500">{item.price.toFixed(2)}</span>
                        <span className="text-xs font-semibold text-green-600">{discountPercentage}% OFF</span>
                      </>
                    )}
                    {item.subcategory && (
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium
                        ${item.subcategory.toLowerCase() === 'veg' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
                          item.subcategory.toLowerCase() === 'non-veg' ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' :
                            'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'}`}>
                        {item.subcategory.toUpperCase()}
                      </span>
                    )}
                  </div>
                </div>

                {/* Add / Cart Controls */}
                <div className="flex items-center justify-between mt-auto">
                  {item.isAvailable ? (
                    (() => {
                      const cartItem = cartItems.find(ci => ci.id === item.id);
                      return cartItem ? (
                        <div className="flex items-center border border-gray-300 dark:border-gray-600 rounded-full h-9 min-w-[88px] bg-white dark:bg-gray-700">
                          <button
                            onClick={() => handleDecrementQuantity(item.id)}
                            className="w-9 h-9 flex items-center justify-center text-red-500 font-medium hover:bg-red-50 dark:hover:bg-gray-600 rounded-full transition"
                          >−</button>
                          <span className="w-7 text-center text-sm font-semibold text-gray-900 dark:text-white">{cartItem.quantity}</span>
                          <button
                            onClick={() => handleIncrementQuantity(item.id)}
                            className="w-9 h-9 flex items-center justify-center text-green-600 font-medium hover:bg-green-50 dark:hover:bg-gray-600 rounded-full transition"
                          >+</button>
                        </div>
                      ) : (
                        <button
                          onClick={() => handleAddToCart(item)}
                          className="px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold rounded-full shadow transition"
                        >ADD</button>
                      );
                    })()
                  ) : (
                    <span className="px-3 py-1 text-xs font-semibold text-red-500 bg-white rounded-full shadow dark:bg-gray-700 dark:text-red-400">Out of Stock</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {filteredItems.length === 0 && (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            <p className="text-sm sm:text-base">No items found matching your criteria</p>
          </div>
        )}

      </div>
    </div>
  );
};

export default NewOrderPage;